'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';

import { useTheme } from '@/theme/ThemeContext';

import { FeedbackForm } from './FeedbackForm';
import { useScene } from './SceneContext';
import { getBreadcrumbSnapshot } from './breadcrumbStore';
import { captureEnv, captureRoute } from './capture';
import type { FeedbackSubmission, FeedbackTrigger, Target } from './ontology/types';

/** Describes the explicit trigger and optional target required to open feedback. */
type OpenFeedbackOptions = {
  /** Stores how the caller opened the feedback flow. */
  trigger: FeedbackTrigger;
  /** Stores the optional entity the caller is targeting with feedback. */
  target?: Target;
};

/** Describes the feedback controls exposed to descendants by FeedbackController. */
interface FeedbackContextValue {
  openFeedback: (options: OpenFeedbackOptions) => void;
}

/** Stores the immutable submission context captured when feedback is opened. */
type FeedbackSnapshot = Omit<FeedbackSubmission, 'tags' | 'unselected_tags' | 'message'>;

/** Holds feedback controls for descendants rendered inside FeedbackController. */
const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

/** Captures point-in-time feedback context and renders the feedback dialog controller. */
export const FeedbackController: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scene = useScene();
  const { mode } = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [snapshot, setSnapshot] = useState<FeedbackSnapshot | null>(null);

  /** Closes the dialog and clears the snapshot captured when feedback was opened. */
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSnapshot(null);
  }, []);

  /** Captures the current feedback context and opens the feedback dialog. */
  const openFeedback = useCallback(
    (options: OpenFeedbackOptions) => {
      const nextSnapshot: FeedbackSnapshot = {
        trigger: options.trigger,
        scene,
        // Read the store imperatively when feedback opens so this snapshot includes
        // breadcrumbs pushed earlier in the same handler, such as a downvote that
        // records a Vote breadcrumb immediately before opening feedback. A hook value
        // tied to the last render could miss that same-tick update and would re-render
        // this subtree on every breadcrumb push even though open-time capture is enough.
        breadcrumbs: getBreadcrumbSnapshot(),
        route: captureRoute(),
        env: captureEnv(mode),
        target: options.target ?? null,
      };

      setSnapshot(nextSnapshot);
      setIsOpen(true);
    },
    [mode, scene]
  );

  return (
    <FeedbackContext.Provider value={{ openFeedback }}>
      {children}
      {snapshot && <FeedbackForm isOpen={isOpen} snapshot={snapshot} onClose={handleClose} />}
    </FeedbackContext.Provider>
  );
};

/** Returns feedback controls and requires a surrounding FeedbackController. */
export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackController');
  }
  return context;
};
